import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import PageRenderer from '../../components/common/PageRenderer';

function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b}`, answer: String(a + b) };
}

function MessageModal({ surveyor, onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaInput.trim() !== captcha.answer) {
      toast.error('Incorrect answer. Please solve the math question.');
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      return;
    }
    setSending(true);
    try {
      await api.post('/messages/submit/', {
        ...formData,
        source: 'surveyor_inquiry',
        surveyor_name: surveyor.full_name,
        surveyor_membership_number: surveyor.membership_number || '',
      });
      toast.success('Message sent successfully!');
      onClose();
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0B5D1E] rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Send a Message</h3>
            <p className="text-white/70 text-xs mt-0.5">
              Inquiry about <span className="font-semibold text-[#C8A951]">{surveyor.full_name}</span>
              {surveyor.membership_number && <span> ({surveyor.membership_number})</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
              <input type="text" required value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Email</label>
              <input type="email" required value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
            <input type="text" required value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="What is this regarding?"
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
            <textarea rows={4} required value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Type your message here..."
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Security Check: What is {captcha.question}?</label>
            <input type="text" required value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              placeholder="Enter your answer"
              className="w-full px-4 py-2.5 bg-[#F8FAFB] border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={sending}
              className="flex-1 bg-[#0B5D1E] hover:bg-[#094A18] disabled:opacity-60 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md flex items-center justify-center gap-2">
              {sending ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
              ) : (
                <>Send Message <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SearchAndResults() {
  const [surveyors, setSurveyors] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchSurveyors = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('membership_category', category);
      params.append('page', pageNum);
      const res = await api.get(`/accounts/find-surveyor/?${params}`);
      setSurveyors(res.data.results || res.data);
      setTotalCount(res.data.count || 0);
      setPage(pageNum);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) fetchSurveyors(1);
  };

  // Auto-search as user types (debounced 400ms, min 2 chars)
  useEffect(() => {
    if (search.trim().length < 2) {
      if (!search.trim()) { setSurveyors([]); setHasSearched(false); setTotalCount(0); }
      return;
    }
    const timer = setTimeout(() => fetchSurveyors(1), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) fetchSurveyors(p);
  };

  return (
    <>
      {/* Search Section */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <form onSubmit={handleSearch} className="bg-[#F8FAFB] rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, membership number, firm, or specialization..."
                  className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition text-sm" />
              </div>
              <button type="submit"
                className="bg-[#0B5D1E] hover:bg-[#094A18] text-white px-8 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all shadow-md flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="pb-16 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {!hasSearched ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Search the Directory</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Enter a name, membership number, firm, or specialization above and click Search to find a surveyor.
              </p>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center py-20">
              <div className="spinner mb-4"></div>
              <p className="text-gray-500 text-sm">Searching directory...</p>
            </div>
          ) : surveyors.length === 0 ? (
            <div className="text-center py-20">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Surveyors Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                No surveyors match your current search criteria. Try adjusting your search terms or browsing all categories.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">
                Showing <strong className="text-gray-900">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)}</strong> of <strong className="text-gray-900">{totalCount}</strong> surveyor{totalCount !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {surveyors.map((s) => (
                  <div key={s.membership_number || s.public_profile_slug}
                    className="bg-[#F8FAFB] rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-5 flex items-center gap-5">
                      {/* Left – Surveyor Photo */}
                      <div className="flex-shrink-0">
                        {s.photo ? (
                          <img src={s.photo} alt={s.full_name}
                            className="w-20 h-20 rounded-full object-cover border-2 border-[#0B5D1E]/20 shadow" />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0B5D1E] to-[#1A7A35] text-white flex items-center justify-center text-2xl font-bold shadow">
                            {s.full_name?.charAt(0)}
                          </div>
                        )}
                      </div>

                      {/* Middle – Details */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <h3 className="font-bold text-[#111827] text-base truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {s.full_name}
                        </h3>
                        {s.firm_name && (
                          <p className="text-sm text-gray-600 truncate flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {s.firm_name}
                          </p>
                        )}
                        {s.email && (
                          <p className="text-sm text-gray-500 truncate flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {s.email}
                          </p>
                        )}
                        {s.phone_number && (
                          <p className="text-sm text-gray-500 truncate flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {s.phone_number}
                          </p>
                        )}
                        {(s.firm_address || s.address || s.city || s.state) && (
                          <p className="text-sm text-gray-500 flex items-start gap-1.5">
                            <svg className="w-3.5 h-3.5 text-[#0B5D1E] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {[s.firm_address || s.address, s.city, s.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                        <div className="flex items-center gap-3 pt-1">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                            {s.membership_status || 'N/A'}
                          </span>
                          {s.membership_status === 'active' && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0B5D1E] bg-[#0B5D1E]/10 px-2 py-0.5 rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Verified Member
                            </span>
                          )}
                          {s.membership_status === 'inactive' && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Inactive Member
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right – Company Logo */}
                      <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-2">
                        {s.company_logo ? (
                          <img src={s.company_logo} alt={`${s.firm_name || ''} logo`}
                            className="w-24 h-24 rounded-lg object-contain border border-gray-200 bg-white p-1 shadow-sm" />
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                            <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="px-5 pb-4">
                      <button onClick={() => setSelectedSurveyor(s)}
                        className="w-full block text-center bg-[#0B5D1E] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#094A18] transition-all shadow-sm cursor-pointer">
                        View Profile & Send Message
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button onClick={() => goToPage(page - 1)} disabled={page === 1}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2))
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '...' ? (
                        <span key={`dots-${idx}`} className="px-2 text-gray-400 text-sm">...</span>
                      ) : (
                        <button key={item} onClick={() => goToPage(item)}
                          className={`w-10 h-10 rounded-lg text-sm font-semibold transition ${
                            item === page
                              ? 'bg-[#0B5D1E] text-white shadow-md'
                              : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}>
                          {item}
                        </button>
                      )
                    )}
                  <button onClick={() => goToPage(page + 1)} disabled={page === totalPages}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {selectedSurveyor && (
        <MessageModal surveyor={selectedSurveyor} onClose={() => setSelectedSurveyor(null)} />
      )}
    </>
  );
}

function DefaultContent() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B5D1E]" style={{ backgroundImage: 'linear-gradient(135deg, #0B5D1E 0%, #094A18 100%)' }}>
        <div className="relative" style={{ paddingTop: 80, paddingBottom: 80, zIndex: 1 }}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, color: '#fff', marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>
              Find a Surveyor
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Search our directory of registered surveyors in the Federal Capital Territory
            </p>
          </div>
        </div>
      </section>

      {/* Search & Results */}
      <SearchAndResults />
    </>
  );
}

export default function FindSurveyor() {
  return (
    <PageRenderer
      pageKey="find_surveyor"
      insertAfter={{
        'Find Surveyor Hero': () => <SearchAndResults />,
      }}
      fallback={<DefaultContent />}
    />
  );
}
